import type { FC } from "react"
import { useId } from "react"
import { InputAdornment, Stack, Typography } from "@mui/material"
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined"
import { DateTimeField } from "@mui/x-date-pickers"
import type { DateTimeValidationError } from "@mui/x-date-pickers"
import type { DateTime } from "luxon"

interface DateRangeFieldProps {
  label: string
  value: DateTime | null
  onChange: (value: DateTime | null) => void
  onError: (error: DateTimeValidationError) => void
  /** Whether the calendar and the clock edit this end. */
  isActive: boolean
  onActivate: () => void
  format?: string
  /** The phone size: slightly bigger text. */
  large: boolean
  disableFuture: boolean
  maxDateTime?: DateTime
  minDateTime?: DateTime
}

/** A typed date field; focusing it points the editor's calendar at this end. */
const DateRangeField: FC<DateRangeFieldProps> = ({
  label,
  isActive,
  onActivate,
  large,
  ...props
}) => {
  const inputId = useId()

  return (
    <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
      {/* Outside the field rather than on its border, as in the design. */}
      <Typography
        component="label"
        htmlFor={inputId}
        variant="body2"
        color={isActive ? "primary" : "text.secondary"}
      >
        {label}
      </Typography>
      <DateTimeField
        {...props}
        id={inputId}
        size="small"
        focused={isActive || undefined}
        onFocus={onActivate}
        sx={{
          mb: 0,
          "& .MuiInputBase-root": {
            backgroundColor: "background.default",
            fontSize: large ? "0.875rem" : undefined,
          },
        }}
        InputProps={{
          endAdornment: (
            // Only a hint: a click goes through to the field and focuses it.
            <InputAdornment position="end" sx={{ pointerEvents: "none" }}>
              <CalendarMonthOutlinedIcon
                fontSize="small"
                sx={{ color: "text.secondary" }}
              />
            </InputAdornment>
          ),
        }}
      />
    </Stack>
  )
}

export default DateRangeField
